import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import {
  useCashReport,
  useMoneyAccounts,
  useSaveAccount,
  useSetAccountArchived,
  type AccountBalance,
  type MoneyAccount,
} from "@/api/cash";
import { useBankAccounts } from "@/api/settings";
import { MoneyAccountForm } from "@/components/forms/MoneyAccountForm";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { formatMoney } from "@/lib/format";
import { formatTotals } from "@/lib/money";

type Editing = { mode: "new" } | { mode: "edit"; account: MoneyAccount } | null;

const KIND_LABEL: Record<MoneyAccount["kind"], string> = {
  bank: "Bank",
  cash: "Cash",
  person: "Person",
  sister_company: "Sister company",
  other: "Other",
};

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-lg border bg-card p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="break-words text-lg font-semibold text-primary">{value}</p>
    </div>
  );
}

export function AccountsPanel() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const accounts = useMoneyAccounts(includeArchived);
  const report = useCashReport();
  const bankAccounts = useBankAccounts(false);
  const save = useSaveAccount();
  const archive = useSetAccountArchived();
  const balances = new Map<string, AccountBalance>(
    (report.data?.accounts ?? []).map((row) => [row.id, row]),
  );

  return (
    <div className="flex flex-col gap-4">
      {report.data && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Figure
            label="At hand (bank + cash)"
            value={report.data.totals.length ? formatTotals(report.data.totals) : "Nothing yet"}
          />
          <Figure
            label="Held at the sister company"
            value={
              report.data.held_at_sister.length
                ? formatTotals(report.data.held_at_sister)
                : "Nothing"
            }
          />
          <Figure
            label="Owed to directors"
            value={
              report.data.owed_to_directors.length
                ? formatTotals(report.data.owed_to_directors)
                : "Nothing"
            }
          />
          <Figure label="Pending transfers" value={String(report.data.pending_transfers)} />
        </div>
      )}
      {report.data && report.data.not_converted.length > 0 && (
        <Alert variant="warning" title={`${formatTotals(report.data.not_converted)} is not placed`}>
          It was received or spent in a currency its account does not hold and has no rupee figure.
          Add the INR credited on the payment, or move it through a transfer at the real rate.
        </Alert>
      )}
      {report.data && (report.data.unplaced_payments > 0 || report.data.unplaced_expenses > 0) && (
        <Alert title="Some older rows name no account">
          {report.data.unplaced_payments} payment(s) and {report.data.unplaced_expenses} expense(s)
          were recorded before accounts existed; balances leave them out. Payments can be placed
          from their bank-details form.
        </Alert>
      )}
      <RequestError error={report.error ?? accounts.error} />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Checkbox
          id="accounts-archived"
          label="Show archived accounts"
          checked={includeArchived}
          onChange={setIncludeArchived}
        />
        <div className="flex gap-2">
          <Link to="/reports?report=cash" className="text-sm text-primary hover:underline">
            Full cash report
          </Link>
          {editing === null && (
            <Button size="sm" onClick={() => setEditing({ mode: "new" })}>
              <Plus className="h-4 w-4" aria-hidden />
              Add account
            </Button>
          )}
        </div>
      </div>
      {editing && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>
              {editing.mode === "new" ? "New account" : `Edit ${editing.account.name}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MoneyAccountForm
              initial={editing.mode === "edit" ? editing.account : null}
              bankAccounts={bankAccounts.data ?? []}
              onSubmit={async (input) => {
                await save.mutateAsync({
                  input,
                  existing: editing.mode === "edit" ? editing.account : undefined,
                });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              onReload={() => {
                void accounts.refetch();
                setEditing(null);
              }}
            />
          </CardContent>
        </Card>
      )}
      <RequestError error={archive.error} />
      <ul className="grid gap-3 md:grid-cols-2">
        {(accounts.data ?? []).map((account) => {
          const row = balances.get(account.id);
          return (
            <li
              key={account.id}
              className="flex flex-col gap-2 rounded-lg border bg-card p-4 shadow-sm"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{account.name}</span>
                <Badge variant="secondary">{KIND_LABEL[account.kind]}</Badge>
                {account.is_default_in && <Badge variant="success">receipts</Badge>}
                {account.is_default_out && <Badge variant="success">payments</Badge>}
                {account.archived_at && <Badge variant="muted">archived</Badge>}
              </div>
              <p className="text-2xl font-semibold tabular-nums text-primary">
                {row ? formatMoney(row.balance, row.currency) : account.archived_at ? "—" : "…"}
              </p>
              {row && (Number(row.pending_in) > 0 || Number(row.pending_out) > 0) && (
                <p className="text-xs text-muted-foreground">
                  Pending: +{formatMoney(row.pending_in, row.currency)} / −
                  {formatMoney(row.pending_out, row.currency)}
                </p>
              )}
              <div className="flex gap-2">
                {!account.archived_at && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing({ mode: "edit", account })}
                  >
                    Edit
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={archive.isPending}
                  onClick={() =>
                    archive.mutate({
                      id: account.id,
                      archived: !account.archived_at,
                      version: account.version,
                    })
                  }
                >
                  {account.archived_at ? "Restore" : "Archive"}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
